import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Row, Col, Form } from 'reactstrap';
import { get, noop } from 'lodash-es';
import classNames from 'classnames';
/* Models */
import { InventoryEntities } from 'client/data/models/inventory';
/* Utils */
import { updateCreativeId } from 'site-modules/financing/utils/update-creative-id';
import { getVehicleType } from 'site-modules/financing/utils/financing-utils';
/* Components */
import { FormField } from 'site-modules/shared/components/form-field/form-field';
import { FormFieldMask } from 'site-modules/shared/components/form-field-mask/form-field-mask';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { FormFieldErrorMessage } from 'site-modules/financing/components/shared/form-field-error-message/form-field-error-message';
import { NextButton } from 'site-modules/financing/components/shared/next-button/next-button';
import { BackButton } from 'site-modules/financing/components/shared/back-button/back-button';
import { FeedbackScreen } from 'site-modules/financing/components/feedback-screen/feedback-screen';
/* Constants */
import {
  PHONE_MASK,
  VALIDATORS,
  SSN_LAST_FOUR_FIELD_MASK,
  DIGIT_CHAR,
} from 'client/site-modules/financing/constants/validation';
import { LOOK_UP_STEP } from 'site-modules/shared/constants/financing/steps';
/* Tracking */
import { EventToolbox } from 'client/utils/event-toolbox';
import { FINANCING_EVENTS } from 'site-modules/shared/constants/financing/financing-events';
/* HOC */
import { connectedPreQualify } from 'site-modules/financing/components/pre-qualify/pre-qualify';

import './pre-qualification-found-step.scss';

export function PreQualificationFoundUI({
  isMatchingVIN = false,
  onSubmit = noop,
  goToPrevScreen = noop,
  isMobile = false,
  vehicle = null,
  isLoadingFeedbackScreen = false,
  isLoading = false,
  isPreVinFlow = false,
  calculatorSelections = {},
}) {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const {
    control,
    formState: { isValid },
    getValues,
    handleSubmit: validateAndSubmit,
    trigger,
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      confirmEmail: '',
      ssn: '',
      zip: '',
    },
  });
  const formValues = useWatch({ control });
  const [isSsnMasked, setIsSsnMasked] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [hasValidatedOnBlur, setHasValidatedOnBlur] = useState(false);

  const handleBlur = async (e, fieldName, onBlur) => {
    onBlur();
    const isValidated = await trigger(fieldName);

    if (fieldName === 'email' && getValues('confirmEmail')) {
      await trigger('confirmEmail');
    }

    setHasValidatedOnBlur(true);
    EventToolbox.fireCustomEvent(FINANCING_EVENTS.INPUT_VALIDATION, {
      target: e.target,
      data: {
        isValidated,
        isPrimary: false,
        name: fieldName,
      },
    });
    setFocusedField(current => (current === fieldName ? null : current));
  };

  const handleSubmit = async (values, event) => {
    const form = event.target;
    await onSubmit({
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: values.phoneNumber,
      email: values.email,
      ssn: values.ssn,
      zip: values.zip,
    });
    form.submit();
    setIsFormSubmitted(true);
  };

  function renderMetaData() {
    const { firstName, lastName, phoneNumber, email, ssn, zip } = formValues;
    const metaData = {
      firstName,
      lastName,
      phoneNumber,
      email,
      zip,
      ssnHidden: ssn,
      vin: get(vehicle, 'vin'),
      make: get(vehicle, 'vehicleInfo.styleInfo.make'),
      model: get(vehicle, 'vehicleInfo.styleInfo.model'),
      year: get(vehicle, 'vehicleInfo.styleInfo.year'),
      condition: getVehicleType(vehicle),
      mileage: get(vehicle, 'vehicleInfo.mileage'),
      salesPrice: get(vehicle, 'prices.advertisedPrice'),
      isCapOne: isMatchingVIN,
      isPreVinFlow,
      calculatorSelections: JSON.stringify(calculatorSelections),
    };

    return Object.keys(metaData).map(key => <input key={key} type="hidden" name={key} value={metaData[key] ?? ''} />);
  }

  return (
    <>
      <iframe
        className="border-0"
        height={0}
        name="pre-qualify-frame"
        sandbox="allow-scripts allow-same-origin"
        title="Pre-Qualify Form"
      />
      <Form
        method="POST"
        id="prequalify-form"
        target="pre-qualify-frame"
        noValidate
        onSubmit={validateAndSubmit(handleSubmit)}
      >
        {isLoadingFeedbackScreen ? (
          <FeedbackScreen activeStep={LOOK_UP_STEP} />
        ) : (
          <>
            <h1 className="size-30 text-center mx-1 mx-md-0">Already Pre-Qualified with Capital One?</h1>
            <Row
              className="pre-qualified-form mx-auto my-0 mt-1"
              data-tracking-parent={updateCreativeId({
                isPreVinFlow,
                isMatchingVIN,
                trackingParent: 'personal_lookup',
              })}
            >
              <Row className="justify-content-center">
                <div className="mb-0_25 fw-bold text-gray-darker size-16 px-1">
                  Enter your information to look up your pre-qualified terms
                </div>
                <div className="size-12 mb-1_5 text-gray-darker px-1 w-100">All fields are required.</div>
              </Row>
              <Row className="px-0_5 w-100">
                <Col md={6}>
                  <Controller
                    control={control}
                    name="firstName"
                    rules={{
                      validate: value => VALIDATORS.firstName.test(value) || VALIDATORS.firstName.errorText,
                    }}
                    render={({ field, fieldState }) => {
                      const error = fieldState.isTouched ? fieldState.error?.message : '';

                      return (
                        <>
                          <FormField
                            {...field}
                            id="first-name"
                            className={classNames({ 'mb-0': error })}
                            type="text"
                            maxLength={255}
                            inputClassName="input-field"
                            isValid={!error}
                            onBlur={e => handleBlur(e, field.name, field.onBlur)}
                            onFocus={() => setFocusedField(field.name)}
                            placeholder="First Name"
                            ariaInvalid={!!error}
                            ariaDescribedBy="first-name-errorMessage"
                            label="First Name"
                            useFloatingLabel
                            autoComplete="given-name"
                          />
                          <FormFieldErrorMessage
                            id="first-name-errorMessage"
                            message={error}
                            className="mt-0_25 mb-1 ps-1"
                          />
                        </>
                      );
                    }}
                  />
                </Col>
                <Col md={6}>
                  <Controller
                    control={control}
                    name="lastName"
                    rules={{ validate: value => VALIDATORS.lastName.test(value) || VALIDATORS.lastName.errorText }}
                    render={({ field, fieldState }) => {
                      const error = fieldState.isTouched ? fieldState.error?.message : '';

                      return (
                        <>
                          <FormField
                            {...field}
                            id="last-name"
                            className={classNames({ 'mb-0': error })}
                            type="text"
                            maxLength={255}
                            inputClassName="input-field"
                            isValid={!error}
                            onBlur={e => handleBlur(e, field.name, field.onBlur)}
                            onFocus={() => setFocusedField(field.name)}
                            placeholder="Last Name"
                            ariaInvalid={!!error}
                            ariaDescribedBy="last-name-errorMessage"
                            label="Last Name"
                            useFloatingLabel
                            autoComplete="family-name"
                          />
                          <FormFieldErrorMessage
                            id="last-name-errorMessage"
                            message={error}
                            className="mt-0_25 mb-1 ps-1"
                          />
                        </>
                      );
                    }}
                  />
                </Col>
              </Row>
              <Row className="px-0_5 w-100">
                <Col xs={12}>
                  <Controller
                    control={control}
                    name="phoneNumber"
                    rules={{
                      validate: value => VALIDATORS.phoneNumber.test(value) || VALIDATORS.phoneNumber.errorText,
                    }}
                    render={({ field, fieldState }) => {
                      const error = fieldState.isTouched ? fieldState.error?.message : '';

                      return (
                        <>
                          <FormFieldMask
                            id="tel"
                            className={classNames({ 'mb-0': error })}
                            type="tel"
                            inputClassName="input-field"
                            ref={field.ref}
                            isValid={!error}
                            onBlur={e => handleBlur(e, field.name, field.onBlur)}
                            onFocus={() => setFocusedField(field.name)}
                            onChange={field.onChange}
                            mask={PHONE_MASK}
                            name={field.name}
                            defaultValue={field.value}
                            ariaInvalid={!!error}
                            ariaDescribedBy="tel-errorMessage"
                            label="Phone Number"
                            useFloatingLabel
                            autoComplete="tel-national"
                            aria-required
                            inputMode="numeric"
                          />
                          <FormFieldErrorMessage id="tel-errorMessage" message={error} className="mt-0_25 mb-1 ps-1" />
                        </>
                      );
                    }}
                  />
                </Col>
              </Row>
              <Row className="px-0_5 w-100">
                <Col xs={12}>
                  <Controller
                    control={control}
                    name="email"
                    rules={{ validate: value => VALIDATORS.email.test(value) || VALIDATORS.email.errorText }}
                    render={({ field, fieldState }) => {
                      const error = fieldState.isTouched ? fieldState.error?.message : '';

                      return (
                        <>
                          <FormField
                            {...field}
                            id="email"
                            className={classNames({ 'mb-0': error })}
                            type="email"
                            maxLength={255}
                            inputClassName="input-field"
                            isValid={!error}
                            onBlur={e => handleBlur(e, field.name, field.onBlur)}
                            onFocus={() => setFocusedField(field.name)}
                            placeholder="Email Address"
                            ariaInvalid={!!error}
                            ariaDescribedBy="email-errorMessage"
                            label="Email Address"
                            useFloatingLabel
                            autoComplete="email"
                            aria-required
                          />
                          <FormFieldErrorMessage
                            id="email-errorMessage"
                            message={error}
                            className="mt-0_25 mb-1 ps-1"
                          />
                        </>
                      );
                    }}
                  />

                  <Controller
                    control={control}
                    name="confirmEmail"
                    rules={{
                      validate: {
                        validEmail: value => VALIDATORS.confirmEmail.test(value) || VALIDATORS.confirmEmail.errorText,
                        matchesEmail: value =>
                          value === getValues('email') || 'Please check that your email is entered correctly.',
                      },
                    }}
                    render={({ field, fieldState }) => {
                      const error = fieldState.isTouched ? fieldState.error?.message : '';

                      return (
                        <>
                          <FormField
                            {...field}
                            id="confirm-email"
                            className={classNames({ 'mb-0': error })}
                            type="email"
                            maxLength={255}
                            inputClassName="input-field"
                            isValid={!error}
                            onBlur={e => handleBlur(e, field.name, field.onBlur)}
                            onFocus={() => setFocusedField(field.name)}
                            placeholder="Confirm Email Address"
                            ariaInvalid={!!error}
                            ariaDescribedBy="confirm-email-errorMessage"
                            label="Confirm Email Address"
                            useFloatingLabel
                            autoComplete="email"
                            aria-required
                          />
                          <FormFieldErrorMessage
                            id="confirm-email-errorMessage"
                            message={error}
                            className="mt-0_25 mb-1 ps-1"
                          />
                        </>
                      );
                    }}
                  />
                </Col>
              </Row>
              <Row className="px-0_5 w-100">
                <Col md={8} className="ssn-field pos-r">
                  <Controller
                    control={control}
                    name="ssn"
                    rules={{
                      validate: value => validation.validateSSNLastFour(value) || 'Please enter 4 digits.',
                    }}
                    render={({ field, fieldState }) => {
                      const error = fieldState.isTouched ? fieldState.error?.message : '';

                      return (
                        <>
                          <FormFieldMask
                            id="ssn"
                            type="text"
                            className={classNames({ 'mb-0': error })}
                            inputClassName="input-field mb-0_5 ps-3"
                            ref={field.ref}
                            isValid={!error}
                            onBlur={e => {
                              setIsSsnMasked(true);
                              handleBlur(e, field.name, field.onBlur);
                            }}
                            onChange={value => {
                              if (!value || DIGIT_CHAR.test(value)) field.onChange(value);
                            }}
                            onFocus={() => {
                              setFocusedField(field.name);
                              setIsSsnMasked(false);
                            }}
                            mask={SSN_LAST_FOUR_FIELD_MASK}
                            defaultValue={isSsnMasked ? field.value.replace(/\d/g, 'X') : field.value}
                            name={field.name}
                            isTouched={fieldState.isTouched}
                            label="Last 4 Digits of your SSN"
                            labelClassName="ms-1_75"
                            useFloatingLabel
                            ariaInvalid={!!error}
                            ariaDescribedBy="ssn-errorMessage"
                            ariaDescribedByAdditional="ssn-helpText"
                            autoComplete="off"
                            aria-required
                            inputMode="numeric"
                          />
                          <i className="icon-lock2 ssn-lock-icon size-20" aria-hidden />
                          <FormFieldErrorMessage id="ssn-errorMessage" message={error} className="mt-0_25 mb-1 ps-1" />
                        </>
                      );
                    }}
                  />
                </Col>
                <Col md={4}>
                  <Controller
                    control={control}
                    name="zip"
                    rules={{ validate: value => VALIDATORS.zip.test(value) || VALIDATORS.zip.errorText }}
                    render={({ field, fieldState }) => {
                      const error = fieldState.isTouched ? fieldState.error?.message : '';

                      return (
                        <>
                          <FormField
                            {...field}
                            id="zip"
                            className={classNames({ 'mb-0': error })}
                            placeholder="ZIP Code"
                            isValid={!error}
                            onBlur={e => handleBlur(e, field.name, field.onBlur)}
                            onFocus={() => setFocusedField(field.name)}
                            inputClassName="input-field"
                            ariaInvalid={!!error}
                            ariaDescribedBy="zip-errorMessage"
                            label="ZIP Code"
                            useFloatingLabel
                            inputMode="numeric"
                            autoComplete="postal-code"
                          />
                          <FormFieldErrorMessage id="zip-errorMessage" message={error} className="mt-0_25 mb-1 ps-1" />
                        </>
                      );
                    }}
                  />
                </Col>
              </Row>
            </Row>
            <hr className="mt-1" />
            <div className="buttons-container d-flex mx-auto px-0_5 justify-content-between">
              <BackButton onClick={goToPrevScreen} />
              <NextButton
                enabledNextBtn={hasValidatedOnBlur && isValid && !focusedField}
                isFullWidth={isMobile}
                isLoading={isLoading}
                isSubmitBtn
              />
            </div>
          </>
        )}
        {!isFormSubmitted && renderMetaData()}
      </Form>
    </>
  );
}

PreQualificationFoundUI.propTypes = {
  vehicle: InventoryEntities.InventoryVin,
  onSubmit: PropTypes.func,
  goToPrevScreen: PropTypes.func,
  isMobile: PropTypes.bool,
  isLoadingFeedbackScreen: PropTypes.bool,
  isLoading: PropTypes.bool,
  isPreVinFlow: PropTypes.bool,
  isMatchingVIN: PropTypes.bool,
  calculatorSelections: PropTypes.shape({}),
};

export const PreQualificationFound = connectedPreQualify(PreQualificationFoundUI);
